import type {
  BendDirection,
  BendOrientation,
  ConfiguratorEstimate,
  ConfiguratorState,
  HolePoint,
  HoleSlotOrientation,
  InternalCutShape,
  OutlineNotchEdge,
} from './configurator-math';

import {
  getGeometryCheck,
  getHoleCutLengthMm,
  getHoleLayout,
  getProductBendLines,
  getProductInternalCuts,
  getProductOutlineNotches,
  getSelectedOptions,
} from './configurator-math';
import { buildOutlineGeometry, type OutlinePoint } from './configurator-outline';

export type GeometryPoint = {
  xMm: number;
  yMm: number;
};

export type GeometryPathRole = 'outline' | 'opening' | 'internalCut' | 'edgeCut';

export type GeometryPath = {
  id: string;
  label: string;
  role: GeometryPathRole;
  points: GeometryPoint[];
  closed: boolean;
  sourceFeatureId?: string;
};

export type FeatureTarget = 'face' | 'edge' | 'outline';
export type FeatureOperation = 'cut' | 'drill' | 'mill' | 'mark';

export type BasePartFeature = {
  id: string;
  label: string;
  enabled: boolean;
  locked?: boolean;
  visible?: boolean;
  target: FeatureTarget;
  operation: FeatureOperation;
  position?: {
    xMm: number;
    yMm: number;
  };
  rotationDeg?: number;
  source?: 'native' | 'legacy-hole' | 'legacy-internal-cut' | 'legacy-outline-notch';
  sourceIndex?: number;
};

export type RoundOpeningFeature = BasePartFeature & {
  kind: 'roundOpening';
  diameterMm: number;
};

export type SlotOpeningFeature = BasePartFeature & {
  kind: 'slotOpening';
  widthMm: number;
  lengthMm: number;
  orientation: HoleSlotOrientation;
  angleDeg: number;
};

export type RectOpeningFeature = BasePartFeature & {
  kind: 'rectOpening';
  widthMm: number;
  heightMm: number;
  cornerRadiusMm?: number;
  angleDeg: number;
};

export type InternalCutFeature = BasePartFeature & {
  kind: 'internalCut';
  shape: InternalCutShape;
  widthMm: number;
  lengthMm: number;
  cornerRadiusMm?: number;
  orientation: HoleSlotOrientation;
  angleDeg: number;
};

export type EdgeCutFeature = BasePartFeature & {
  kind: 'edgeCut';
  edge: OutlineNotchEdge;
  centerMm: number;
  widthMm: number;
  depthMm: number;
  cornerRadiusMm?: number;
};

export type PartFeature =
  | RoundOpeningFeature
  | SlotOpeningFeature
  | RectOpeningFeature
  | InternalCutFeature
  | EdgeCutFeature;

export type PartBendLine = {
  id: string;
  label: string;
  orientation: BendOrientation;
  positionMm: number;
  angleDeg: number;
  direction: BendDirection;
  enabled: boolean;
};

export type PartOutline = {
  type: 'polygon';
  widthMm: number;
  heightMm: number;
  path: GeometryPoint[];
};

export type ManufacturingSettings = {
  process: string;
  warnings: string[];
  manufacturabilityScore: number;
  minRequiredHoleMm: number;
  minRequiredEdgeMm: number;
  minRequiredPitchMm: number;
};

export type PartMetadata = {
  productId: string;
  productName: string;
  machineId: string;
  materialId: string;
  finishId: string;
};

export type PartGeometry = {
  units: 'mm';
  outline: PartOutline;
  features: PartFeature[];
  bendLines: PartBendLine[];
  manufacturing: ManufacturingSettings;
  metadata: PartMetadata;
};

export type ExportEntity =
  | {
      id: string;
      kind: 'outline';
      path: GeometryPath;
    }
  | {
      id: string;
      kind: PartFeature['kind'];
      feature: PartFeature;
      path?: GeometryPath;
    }
  | {
      id: string;
      kind: 'bendLine';
      bendLine: PartBendLine;
    };

export type ResolvedPartGeometry = PartGeometry & {
  finalOutlinePath: GeometryPath;
  openingPaths: GeometryPath[];
  cutPaths: GeometryPath[];
  bendLines: PartBendLine[];
  boundingBox: {
    minXmm: number;
    minYmm: number;
    maxXmm: number;
    maxYmm: number;
    widthMm: number;
    heightMm: number;
  };
  areaMm2: number;
  cutLengthMm: number;
  warnings: string[];
  exportEntities: ExportEntity[];
};

function toGeometryPoints(points: OutlinePoint[]): GeometryPoint[] {
  return points.map(point => ({ xMm: point.xMm, yMm: point.yMm }));
}

function pathLength(points: GeometryPoint[], closed = true) {
  if (points.length < 2) return 0;

  let length = 0;
  const limit = closed ? points.length : points.length - 1;

  for (let index = 0; index < limit; index += 1) {
    const current = points[index];
    const next = points[(index + 1) % points.length];
    length += Math.hypot(next.xMm - current.xMm, next.yMm - current.yMm);
  }

  return length;
}

function polygonArea(points: GeometryPoint[]) {
  if (points.length < 3) return 0;

  const sum = points.reduce((total, point, index) => {
    const next = points[(index + 1) % points.length];
    return total + point.xMm * next.yMm - next.xMm * point.yMm;
  }, 0);

  return Math.abs(sum) / 2;
}

function roundedRectPoints(
  centerXmm: number,
  centerYmm: number,
  widthMm: number,
  heightMm: number,
  radiusMm: number,
  angleDeg = 0,
  steps = 8,
) {
  const width = Math.max(widthMm, 0.1);
  const height = Math.max(heightMm, 0.1);
  const radius = Math.min(Math.max(radiusMm, 0), width / 2, height / 2);
  const halfWidth = width / 2;
  const halfHeight = height / 2;
  const angle = (angleDeg * Math.PI) / 180;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const points: GeometryPoint[] = [];
  const addLocalPoint = (xMm: number, yMm: number) => {
    points.push({
      xMm: centerXmm + xMm * cos - yMm * sin,
      yMm: centerYmm + xMm * sin + yMm * cos,
    });
  };

  if (radius <= 0.001) {
    addLocalPoint(-halfWidth, -halfHeight);
    addLocalPoint(halfWidth, -halfHeight);
    addLocalPoint(halfWidth, halfHeight);
    addLocalPoint(-halfWidth, halfHeight);
    return points;
  }

  const addCorner = (cx: number, cy: number, start: number, end: number) => {
    for (let index = 0; index <= steps; index += 1) {
      const theta = start + ((end - start) * index) / steps;
      addLocalPoint(cx + Math.cos(theta) * radius, cy + Math.sin(theta) * radius);
    }
  };

  addCorner(halfWidth - radius, -halfHeight + radius, -Math.PI / 2, 0);
  addCorner(halfWidth - radius, halfHeight - radius, 0, Math.PI / 2);
  addCorner(-halfWidth + radius, halfHeight - radius, Math.PI / 2, Math.PI);
  addCorner(-halfWidth + radius, -halfHeight + radius, Math.PI, Math.PI * 1.5);

  return points;
}

function circlePoints(centerXmm: number, centerYmm: number, diameterMm: number, steps = 48) {
  const radius = Math.max(diameterMm / 2, 0.05);

  return Array.from({ length: steps }, (_, index) => {
    const theta = (Math.PI * 2 * index) / steps;
    return {
      xMm: centerXmm + Math.cos(theta) * radius,
      yMm: centerYmm + Math.sin(theta) * radius,
    };
  });
}

function holeFeatureFromLegacy(hole: HolePoint, index: number): PartFeature {
  const base = {
    id: `hole-${index + 1}`,
    label: `Otvor ${index + 1}`,
    enabled: true,
    visible: true,
    target: 'face' as const,
    operation: 'cut' as const,
    position: { xMm: hole.xMm, yMm: hole.yMm },
    rotationDeg: hole.slotAngleDeg,
    source: 'legacy-hole' as const,
    sourceIndex: index,
  };

  if (hole.shape === 'slot') {
    return {
      ...base,
      kind: 'slotOpening',
      widthMm: hole.slotWidthMm,
      lengthMm: hole.slotLengthMm,
      orientation: hole.slotOrientation,
      angleDeg: hole.slotAngleDeg,
    };
  }

  if (hole.shape === 'rect') {
    return {
      ...base,
      kind: 'rectOpening',
      widthMm: hole.slotLengthMm,
      heightMm: hole.slotWidthMm,
      angleDeg: hole.slotAngleDeg,
      cornerRadiusMm: 0,
    };
  }

  return {
    ...base,
    kind: 'roundOpening',
    diameterMm: hole.diameterMm,
  };
}

function featurePath(feature: PartFeature): GeometryPath | undefined {
  if (!feature.enabled) return undefined;

  if (feature.kind === 'roundOpening') {
    const center = feature.position ?? { xMm: 0, yMm: 0 };
    return {
      id: `${feature.id}-path`,
      label: feature.label,
      role: 'opening',
      points: circlePoints(center.xMm, center.yMm, feature.diameterMm),
      closed: true,
      sourceFeatureId: feature.id,
    };
  }

  if (feature.kind === 'slotOpening') {
    const center = feature.position ?? { xMm: 0, yMm: 0 };
    return {
      id: `${feature.id}-path`,
      label: feature.label,
      role: 'opening',
      points: roundedRectPoints(
        center.xMm,
        center.yMm,
        feature.lengthMm,
        feature.widthMm,
        feature.widthMm / 2,
        feature.angleDeg,
      ),
      closed: true,
      sourceFeatureId: feature.id,
    };
  }

  if (feature.kind === 'rectOpening') {
    const center = feature.position ?? { xMm: 0, yMm: 0 };
    return {
      id: `${feature.id}-path`,
      label: feature.label,
      role: 'opening',
      points: roundedRectPoints(
        center.xMm,
        center.yMm,
        feature.widthMm,
        feature.heightMm,
        feature.cornerRadiusMm ?? 0,
        feature.angleDeg,
      ),
      closed: true,
      sourceFeatureId: feature.id,
    };
  }

  if (feature.kind === 'internalCut') {
    const center = feature.position ?? { xMm: 0, yMm: 0 };
    const points =
      feature.shape === 'round'
        ? circlePoints(center.xMm, center.yMm, feature.widthMm)
        : roundedRectPoints(
            center.xMm,
            center.yMm,
            feature.lengthMm,
            feature.widthMm,
            feature.cornerRadiusMm ?? (feature.shape === 'slot' ? feature.widthMm / 2 : 0),
            feature.angleDeg,
          );

    return {
      id: `${feature.id}-path`,
      label: feature.label,
      role: 'internalCut',
      points,
      closed: true,
      sourceFeatureId: feature.id,
    };
  }

  if (feature.kind === 'edgeCut') {
    const radius = feature.cornerRadiusMm ?? 0;
    const points =
      feature.edge === 'top' || feature.edge === 'bottom'
        ? roundedRectPoints(
            feature.centerMm,
            feature.edge === 'top' ? feature.depthMm / 2 : -feature.depthMm / 2,
            feature.widthMm,
            feature.depthMm,
            radius,
          )
        : roundedRectPoints(
            feature.edge === 'left' ? feature.depthMm / 2 : -feature.depthMm / 2,
            feature.centerMm,
            feature.depthMm,
            feature.widthMm,
            radius,
          );

    return {
      id: `${feature.id}-path`,
      label: feature.label,
      role: 'edgeCut',
      points,
      closed: true,
      sourceFeatureId: feature.id,
    };
  }

  return undefined;
}

function internalCutFeatureFromLegacy(
  cut: ReturnType<typeof getProductInternalCuts>[number],
  index: number,
): InternalCutFeature {
  return {
    id: cut.id,
    label: cut.label || `Rez ${index + 1}`,
    enabled: true,
    visible: true,
    target: 'face',
    operation: 'cut',
    position: { xMm: cut.xMm, yMm: cut.yMm },
    rotationDeg: cut.angleDeg,
    source: 'legacy-internal-cut',
    sourceIndex: index,
    kind: 'internalCut',
    shape: cut.shape,
    widthMm: cut.widthMm,
    lengthMm: cut.lengthMm,
    cornerRadiusMm: cut.cornerRadiusMm,
    orientation: cut.orientation,
    angleDeg: cut.angleDeg,
  };
}

function edgeCutFeatureFromLegacy(
  notch: ReturnType<typeof getProductOutlineNotches>[number],
  index: number,
): EdgeCutFeature {
  return {
    id: notch.id,
    label: notch.label || `Výrez z hrany ${index + 1}`,
    enabled: true,
    visible: true,
    target: 'edge',
    operation: 'cut',
    source: 'legacy-outline-notch',
    sourceIndex: index,
    kind: 'edgeCut',
    edge: notch.edge,
    centerMm: notch.centerMm,
    widthMm: notch.widthMm,
    depthMm: notch.depthMm,
    cornerRadiusMm: notch.radiusMm,
  };
}

function getBoundingBox(points: GeometryPoint[]) {
  if (!points.length) {
    return { minXmm: 0, minYmm: 0, maxXmm: 0, maxYmm: 0, widthMm: 0, heightMm: 0 };
  }

  const minXmm = Math.min(...points.map(point => point.xMm));
  const minYmm = Math.min(...points.map(point => point.yMm));
  const maxXmm = Math.max(...points.map(point => point.xMm));
  const maxYmm = Math.max(...points.map(point => point.yMm));

  return {
    minXmm,
    minYmm,
    maxXmm,
    maxYmm,
    widthMm: maxXmm - minXmm,
    heightMm: maxYmm - minYmm,
  };
}

export function legacyConfigToPartGeometry(config: ConfiguratorState): PartGeometry {
  const selected = getSelectedOptions(config);
  const outlineGeometry = buildOutlineGeometry(config);
  const outlinePath = toGeometryPoints(outlineGeometry.points);
  const holeFeatures = getHoleLayout(config).map(holeFeatureFromLegacy);
  const internalCutFeatures = getProductInternalCuts(config).map(internalCutFeatureFromLegacy);
  const edgeCutFeatures = getProductOutlineNotches(config).map(edgeCutFeatureFromLegacy);
  const geometryCheck = getGeometryCheck(config);

  return {
    units: 'mm',
    outline: {
      type: 'polygon',
      widthMm: outlineGeometry.widthMm,
      heightMm: outlineGeometry.lengthMm,
      path: outlinePath,
    },
    features: [...holeFeatures, ...internalCutFeatures, ...edgeCutFeatures],
    bendLines: getProductBendLines(config).map(line => ({
      ...line,
      enabled: true,
    })),
    manufacturing: {
      process: selected.machine.process,
      warnings: geometryCheck.warnings,
      manufacturabilityScore: geometryCheck.manufacturabilityScore,
      minRequiredHoleMm: geometryCheck.minRequiredHoleMm,
      minRequiredEdgeMm: geometryCheck.minRequiredEdgeMm,
      minRequiredPitchMm: geometryCheck.minRequiredPitchMm,
    },
    metadata: {
      productId: selected.product.id,
      productName: selected.product.name,
      machineId: selected.machine.id,
      materialId: selected.material.id,
      finishId: selected.finish.id,
    },
  };
}

export function resolvePartGeometry(config: ConfiguratorState): ResolvedPartGeometry {
  const partGeometry = legacyConfigToPartGeometry(config);
  const finalOutlinePath: GeometryPath = {
    id: 'outline',
    label: 'Vonkajší obrys',
    role: 'outline',
    points: partGeometry.outline.path,
    closed: true,
  };
  const featurePaths = partGeometry.features
    .map(feature => ({ feature, path: featurePath(feature) }))
    .filter((item): item is { feature: PartFeature; path: GeometryPath } => Boolean(item.path));
  const openingPaths = featurePaths
    .filter(item => item.path.role === 'opening')
    .map(item => item.path);
  const cutPaths = featurePaths
    .filter(item => item.path.role === 'internalCut' || item.path.role === 'edgeCut')
    .map(item => item.path);
  const openingAreaMm2 = openingPaths.reduce((sum, path) => sum + polygonArea(path.points), 0);
  const internalCutAreaMm2 = cutPaths
    .filter(path => path.role === 'internalCut')
    .reduce((sum, path) => sum + polygonArea(path.points), 0);
  const areaMm2 = Math.max(
    polygonArea(finalOutlinePath.points) - openingAreaMm2 - internalCutAreaMm2,
    0,
  );
  const openingCutLengthMm = getHoleLayout(config).reduce(
    (sum, hole) => sum + getHoleCutLengthMm(hole),
    0,
  );
  const internalCutLengthMm = cutPaths
    .filter(path => path.role === 'internalCut')
    .reduce((sum, path) => sum + pathLength(path.points, path.closed), 0);

  return {
    ...partGeometry,
    finalOutlinePath,
    openingPaths,
    cutPaths,
    bendLines: partGeometry.bendLines,
    boundingBox: getBoundingBox(finalOutlinePath.points),
    areaMm2,
    cutLengthMm:
      pathLength(finalOutlinePath.points, true) + openingCutLengthMm + internalCutLengthMm,
    warnings: partGeometry.manufacturing.warnings,
    exportEntities: [
      { id: finalOutlinePath.id, kind: 'outline', path: finalOutlinePath },
      ...featurePaths.map(({ feature, path }) => ({
        id: feature.id,
        kind: feature.kind,
        feature,
        path,
      })),
      ...partGeometry.bendLines.map(bendLine => ({
        id: bendLine.id,
        kind: 'bendLine' as const,
        bendLine,
      })),
    ],
  };
}

export function calculateResolvedEstimate(config: ConfiguratorState): ConfiguratorEstimate {
  const resolvedGeometry = resolvePartGeometry(config);
  const { machine, finish, material, product } = getSelectedOptions(config);
  const areaM2 = resolvedGeometry.areaMm2 / 1_000_000;
  const weightKg = Math.max(areaM2 * config.thicknessMm * (material.densityKgM3 / 1000), 0.05);
  const cutLengthM = resolvedGeometry.cutLengthMm / 1000;
  const cutFeatureCount = resolvedGeometry.features.filter(
    feature => feature.enabled && feature.kind !== 'edgeCut',
  ).length;
  const bendLines = resolvedGeometry.bendLines.filter(line => line.enabled);
  const cuttingPrice =
    cutLengthM * machine.cutPricePerMeter + cutFeatureCount * machine.holeCutPrice;
  const bendPrice = bendLines.length > 0 ? machine.bendPrice * bendLines.length : 0;
  const unitPrice =
    (weightKg * material.pricePerKg + cuttingPrice + bendPrice + machine.setupPrice) *
    finish.priceMultiplier;
  const totalWeightKg = weightKg * config.quantity;
  const leadTimeDays = Math.max(
    finish.leadTimeDays,
    bendLines.length > 0 || product.operations.includes('bending') ? 3 : 1,
  );

  return {
    areaM2,
    weightKg,
    totalWeightKg,
    unitPrice,
    totalPrice: unitPrice * config.quantity,
    cutLengthM,
    leadTimeDays,
  };
}
